import { Arg, Entity, Field, Mutation } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { baseConfig } from '@adornis/config/baseConfig.js';
import { sendDigitaleHeldenMail } from '@adornis/digitale-helden-shared/features/mailing/server/mailer.js';
import { Recipient } from '@adornis/mails/db/recipients.js';
import { Message } from '@adornis/mails/server/communication.js';
import { validate } from '@adornis/validation/decorators.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import { ThrowNonOptionalError, validateEmail, validateEmailRepeat, validateNonOptional } from 'db/helpers';
import { DateTime } from 'luxon';
import { ProductType } from '../../../db/product/enums.js';
import { getProductByID } from '../../../db/product/queries/getProductByID.js';
import { supportFormTemplate } from '../../../features/mailing/server/templates/support-form-template.js';
import { SupportTopic, SupportTopicWithProductNeed as SupportTopicsWithProductNeed } from './enums.js';
import { supportFormularTranslations } from './translations.js';

@Entity()
export class FormularSupport extends AdornisEntity {
  static override _class = 'FormularSupport';

  @validate(nonOptional())
  @Field(type => String)
  topic!: SupportTopic;

  @validate(options => {
    if (options.value) return;
    if (!SupportTopicsWithProductNeed.includes(options.target.topic)) return;
    ThrowNonOptionalError(options.key);
  })
  @Field(type => String)
  productID!: string;

  @validate(nonOptional())
  @Field(type => String)
  firstName!: string;

  @validate(nonOptional())
  @Field(type => String)
  lastName!: string;

  @validate(options => {
    validateNonOptional({ key: options.key, value: options.value });
    validateEmail({ key: options.key, value: options.value });
  })
  @Field(type => String)
  email!: string;

  @validate(options => {
    validateNonOptional({ key: options.key, value: options.value });
    validateEmailRepeat({ key: options.key, email: options.target.email, emailRepeat: options.value });
  })
  @Field(type => String)
  emailRepeat!: string;

  @validate(nonOptional())
  @Field(type => String)
  message!: string;

  @validate(nonOptional())
  @Field(type => Boolean)
  hasAcceptedPrivacy!: boolean;

  @Mutation(type => String)
  static sendSupportEmail(@Arg('data', type => FormularSupport) data: FormularSupport) {
    return async () => {
      const topicTranslated = supportFormularTranslations.topic[data.topic][0];
      const product = SupportTopicsWithProductNeed.includes(data.topic)
        ? await getProductByID(data.productID)({ name: 1, id: 1, type: 1 })
        : null;

      await sendDigitaleHeldenMail({
        message: async (webversionLink: string) =>
          await Message.compose(
            await supportFormTemplate({
              subject: `Support - ${topicTranslated} - ${DateTime.now().toLocaleString(DateTime.DATE_MED)}`,
              firstName: data.firstName,
              lastName: data.lastName,
              email: data.email,
              message: data.message,
              webmailLink: webversionLink,
              product: product ?? undefined,
            }),
          ),
        recipients:
          baseConfig.get('DEPLOYMENT') === 'production'
            ? getRecipientsByTopicOrType(data.topic, product?.type)
            : new Recipient(['testing+support@novopol.firma.cc', 'michael@adornis.de']),
      });
    };
  }
}

function getRecipientsByTopicOrType(topic: SupportTopic, type?: ProductType) {
  if (type === ProductType.PEER_PROGRAMM) return new Recipient(['mentorenprogramm@digitale-helden.de']);
  switch (topic) {
    case SupportTopic.TECHNICAL_ISSUE:
      return new Recipient(['support@digitale-helden.de']);
    default:
      return new Recipient(['info@digitale-helden.de']);
  }
}
